/**
 * BaselineWelcome — the first-run "here's your starting point" brief (WO-9,
 * reworked 2026-07-10 from the original bless-gate ceremony).
 *
 * A born-Hybrid tenant's baseline is the monitor snapshot Guardian took at
 * onboarding — nothing gates on customer approval. On the customer's first
 * visit to the drift page this brief renders instead of the queue, exactly
 * once: an action-first brief (no fabricated score — see
 * baseline-review-brief.ts), the since-baseline delta grouped by workload area
 * (READ-ONLY — the rows are ordinary drift, already live in the queue and
 * already counted everywhere), and ONE CTA ("Open your drift queue") that
 * records a server-side acknowledgment (who saw the starting point, when) and
 * hands over to the normal queue.
 *
 * Carbon: the brief is a BriefHero with an h2 headline (`headingLevel`),
 * because the page's pillar brief above it already carries the page's one h1;
 * the delta is a read-only DataTable grouped by workload area.
 */

import { useMemo } from 'react'
import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import { api, tcmApi } from '@/lib/api'
import { getServerMessage } from '@/lib/api-error'
import {
  normalizeToUnifiedRow,
  normalizeTcmToUnifiedRow,
  type UnifiedDriftRow,
} from '@/types/unifiedDrift'
import { prettifyResourceType, workloadLabelForResourceType } from '@/lib/tcmResourceNames'
import { BASELINE_REVIEW_CTA, composeBaselineReviewBrief } from '@/lib/baseline-review-brief'
import { BriefHero } from '@/components/carbon/BriefHero'
import { Button } from '@/components/carbon/Button'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { IconArrowRight } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Marked } from '@/components/carbon/Marked'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { driftSeverityLevel, SEVERITY_RANK } from '@/components/BaselineCompliance/drift-carbon-helpers'

/** Active-status set mirrors the Hybrid tab so the brief previews exactly the
 *  rows the queue shows. */
const DSC_ACTIVE_STATUSES = 'New,Pending,PendingApproval,AwaitingRemediation'
const PAGE_SIZE = 200

interface BaselineWelcomeProps {
  tenantId: string
  /** The tenant's last real assessment (`lastAssessmentAt`), for the provenance line. */
  assessedAt?: string | null
  /** Fired once the acknowledgment lands (or was already recorded). The parent
   *  swaps to the normal drift queue. */
  onContinue: () => void
}

const COLUMNS: DataColumn<UnifiedDriftRow>[] = [
  {
    id: 'severity',
    header: 'Severity',
    nowrap: true,
    cell: (row) => {
      const level = driftSeverityLevel(row.severity)
      return level ? <SeverityGlyph level={level}>{row.severity}</SeverityGlyph> : <span className="text-text-helper">—</span>
    },
  },
  {
    id: 'setting',
    header: 'Setting',
    primary: true,
    cell: (row) => (
      <span className="block min-w-0 [overflow-wrap:anywhere]">
        <TableItem
          title={row.propertyPath ? `${prettifyResourceType(row.resourceType)} · ${row.propertyPath}` : prettifyResourceType(row.resourceType)}
          sub={row.resourceName && row.resourceName !== row.resourceType ? row.resourceName : undefined}
        />
      </span>
    ),
  },
]

export default function BaselineWelcome({
  tenantId,
  assessedAt,
  onContinue,
}: BaselineWelcomeProps) {
  // ── Since-baseline delta, both sources (RealChange only — same as the queue) ──
  const dscQuery = useQuery({
    queryKey: ['baseline-review-dsc', tenantId],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId,
        status: DSC_ACTIVE_STATUSES,
        page: 1,
        limit: PAGE_SIZE,
        sortBy: 'severity',
      }),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })

  const tcmQuery = useQuery({
    queryKey: ['baseline-review-tcm', tenantId],
    queryFn: () =>
      tcmApi.listDrifts(tenantId, { state: 'Active', page: 1, pageSize: PAGE_SIZE, sortBy: 'severity' }),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })

  const isLoading = dscQuery.isLoading || tcmQuery.isLoading

  const rows = useMemo<UnifiedDriftRow[]>(() => {
    const dsc = (dscQuery.data?.items ?? []).map(normalizeToUnifiedRow)
    const tcm = (tcmQuery.data?.items ?? []).map(normalizeTcmToUnifiedRow)
    return [...dsc, ...tcm]
  }, [dscQuery.data?.items, tcmQuery.data?.items])

  // Group by workload area. Real areas only, never an invented one.
  const groups = useMemo(() => {
    const byArea = new Map<string, UnifiedDriftRow[]>()
    for (const r of rows) {
      const area = workloadLabelForResourceType(r.resourceType) || 'Other'
      const list = byArea.get(area) ?? []
      list.push(r)
      byArea.set(area, list)
    }
    // Sort rows within each area by severity, and areas by size desc.
    for (const list of byArea.values()) {
      list.sort(
        (a, b) => (SEVERITY_RANK[a.severity ?? 'Info'] ?? 9) - (SEVERITY_RANK[b.severity ?? 'Info'] ?? 9),
      )
    }
    return Array.from(byArea.entries()).sort((a, b) => b[1].length - a[1].length)
  }, [rows])

  // The table's rows in area order, so each area's group reads as one block.
  const orderedRows = useMemo(() => groups.flatMap(([, list]) => list), [groups])

  const itemCount = rows.length
  const worthALook = useMemo(
    () => rows.filter(r => r.severity === 'Critical' || r.severity === 'High').length,
    [rows],
  )

  const brief = useMemo(
    () =>
      composeBaselineReviewBrief({
        tenantId,
        assessedAt,
        itemCount: isLoading ? null : itemCount,
        worthALook,
      }),
    [tenantId, assessedAt, isLoading, itemCount, worthALook],
  )

  // ── Acknowledge-and-continue. The stamp is the audit artifact (who saw the
  // starting point, when); the queue itself was never gated on it. Idempotent
  // server-side, so a double-click or a second user is harmless.
  const acknowledgeMutation = useMutation({
    mutationFn: () => api.acknowledgeBaseline(tenantId),
    onSuccess: () => {
      onContinue()
    },
    onError: err => {
      toast.error(
        getServerMessage(err, 'Could not record that just now. Try again in a moment.'),
        { duration: 6000 },
      )
    },
  })

  const acknowledgePending = acknowledgeMutation.isPending

  return (
    <div id="panel-drifts" role="tabpanel" aria-labelledby="tab-drifts" className="flex flex-col gap-8">
      {/* ── The welcome brief: action-first headline, one lede, provenance.
          No score tile: there is no health score on day 1 (fabrication rule). ── */}
      <section aria-labelledby="baseline-welcome-headline" className="max-w-[56rem]">
        <BriefHero
          brief={brief}
          size="pillar"
          headingLevel="h2"
          headingId="baseline-welcome-headline"
          provenance={<Marked text={brief.byline} />}
        />
      </section>

      {/* ── One action: nothing to approve. ── */}
      <InlineNotification
        kind="info"
        title="Ready when you are."
        actions={
          <Button
            kind="primary"
            size="md"
            icon={<IconArrowRight />}
            onClick={() => acknowledgeMutation.mutate()}
            loading={acknowledgePending}
          >
            {acknowledgePending ? 'Opening' : BASELINE_REVIEW_CTA}
          </Button>
        }
      >
        {isLoading
          ? 'Guardian notes that you have seen this starting point.'
          : itemCount === 0
            ? 'From here, any change to your tenant surfaces as drift.'
            : 'These items are already in your queue. Guardian notes that you have seen this starting point.'}
      </InlineNotification>

      {/* ── The since-baseline delta, grouped by workload area. Read-only:
          these rows live in the queue; this is a preview, not a work surface. ── */}
      <DataTable
        title="Changes since your baseline"
        description="Already in your queue. Grouped by workload area, most severe first."
        columns={COLUMNS}
        rows={orderedRows}
        rowId={(row) => `${row.source}:${row.id}`}
        groups={{
          key: (row) => workloadLabelForResourceType(row.resourceType) || 'Other',
          header: (area, list) => (
            <span className="flex flex-wrap items-center gap-x-4">
              {area}
              <span className="text-helper font-normal tabular-nums text-text-helper">
                {list.length} {list.length === 1 ? 'change' : 'changes'}
              </span>
            </span>
          ),
          defaultExpanded: true,
          expandAll: true,
        }}
        isLoading={isLoading}
        empty="Nothing has changed since your baseline."
      />
    </div>
  )
}
